'use strict';

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

class Top {
	constructor(){
		this.render();
	}

	render(){
		new Fv();
		new TopTitles();
		const cycle = new CycleReveal();
		new FvScroll(cycle);
		new AboutScroll();
	}
}

/**
 * TOPの見出し（.top_ttlJp / .top_ttlEn）を1文字ずつ span に割る。
 *  下層ページの .c_pagettl_en と同じ .c_splitChar / .c_splitWord に割り、
 *  CSS 側（_top.scss）が --i を遅延に使って左から順にスライドさせる。
 *  発火は各 hgroup(.top_ttl.scrollEffect) に is-visible が付いたとき。
 *
 *  hgroup ごとに --i を 0 から振り直すので、見出しが画面に入るたび
 *  その見出しの中だけで和文→英字の順に左から流れる。
 *  .top_ttlIcon（★）は .t_hl の外にあるので触らない＝そのまま表示。
 */
class TopTitles {
	constructor(){
		this.groups = Array.from(document.querySelectorAll('.top_ttl'));
		if( !this.groups.length ) return;
		if( window.matchMedia('(prefers-reduced-motion: reduce)').matches ) return;
		this.render();
	}

	render(){
		this.groups.forEach((group)=> this.split(group));
		// FVの主見出しも同じ動き（発火は .top_fv.is-visible）
		this.splitFvTtl();
	}

	// 対象の最後の .c_splitChar の --i を --mi として持たせる。
	// 黄色い線（下線・マーカー）を「その文字列が入り終わってから」引くための基準。
	setMarkerIndex(el){
		const chars = el.querySelectorAll('.c_splitChar');
		if( chars.length ) {
			el.style.setProperty('--mi', chars[chars.length - 1].style.getPropertyValue('--i'));
		}
	}

	// FV主見出し（.top_fv_ttl）。.__l / .__s のネストは保ったまま中の文字だけ割る。
	// マーカーは .__l 単位なので、.__l ごとに --mi を持たせる。
	splitFvTtl(){
		const ttl = document.querySelector('.top_fv_ttl');
		if( !ttl ) return;
		let i = 0;
		const fill = (frag, text)=>{
			const units = [];
			Array.from(text).forEach((c)=>{
				const last = units[units.length - 1];
				if( /[0-9A-Za-z]/.test(c) && last && /^[0-9A-Za-z]+$/.test(last) ) {
					units[units.length - 1] = last + c;
				} else {
					units.push(c);
				}
			});
			units.forEach((u)=>{
				const span = document.createElement('span');
				span.className = 'c_splitChar';
				span.style.setProperty('--i', i++);
				span.textContent = u;
				frag.appendChild(span);
			});
		};
		const walk = (node)=>{
			Array.from(node.childNodes).forEach((child)=>{
				if( child.nodeType === Node.TEXT_NODE ) {
					if( !child.textContent ) return;
					const frag = document.createDocumentFragment();
					fill(frag, child.textContent);
					child.replaceWith(frag);
				} else if( child.nodeType === Node.ELEMENT_NODE && child.tagName !== 'BR' ) {
					walk(child);
				}
			});
		};
		// .t_hl 単位で歩く。h1 直下のインデント（空白テキスト）を拾わないため。
		// i は行をまたいで連番なので、上の行から順に流れる
		ttl.querySelectorAll('.t_hl').forEach((hl)=> walk(hl));
		// .__l ごとに、その中の最後の文字を基準にマーカーを引かせる
		ttl.querySelectorAll('.__l').forEach((el)=> this.setMarkerIndex(el));
		// 伏せCSSを有効化（JSが割ったときだけ。no-JS/reduced-motion では素の表示）
		ttl.classList.add('is-split');
	}

	split(group){
		// DOM 順で拾う（和文 h2 が先、英字 p があと）
		const boxes = group.querySelectorAll('.top_ttlJp, .top_ttlEn');
		if( !boxes.length ) return;
		let i = 0;

		const charSpan = (text)=>{
			const span = document.createElement('span');
			span.className = 'c_splitChar';
			span.style.setProperty('--i', i++);
			span.textContent = text;
			return span;
		};

		// 和文：英数字の連続だけ1かたまりにまとめる（AI / DX が語の途中で折れないように）
		const fillJp = (frag, text)=>{
			const units = [];
			Array.from(text).forEach((c)=>{
				const last = units[units.length - 1];
				if( /[0-9A-Za-z]/.test(c) && last && /^[0-9A-Za-z]+$/.test(last) ) {
					units[units.length - 1] = last + c;
				} else {
					units.push(c);
				}
			});
			units.forEach((u)=>{ frag.appendChild(charSpan(u)); });
		};

		// 英字：1文字ずつ。空白以外の連なりは .c_splitWord でくくって折り返しを防ぐ
		const fillEn = (frag, text)=>{
			const chunks = text.match(/\s+|\S+/g);
			if( !chunks ) return;
			chunks.forEach((chunk)=>{
				if( /^\s+$/.test(chunk) ) {
					Array.from(chunk).forEach((c)=>{ frag.appendChild(charSpan(c)); });
					return;
				}
				const word = document.createElement('span');
				word.className = 'c_splitWord';
				Array.from(chunk).forEach((c)=>{ word.appendChild(charSpan(c)); });
				frag.appendChild(word);
			});
		};

		boxes.forEach((box)=>{
			// 文字は .t_hl の中。★アイコンは .t_hl の外なので触らない
			const root = box.querySelector('.t_hl') || box;
			const isEn = box.classList.contains('top_ttlEn');
			const walk = (node)=>{
				Array.from(node.childNodes).forEach((child)=>{
					if( child.nodeType === Node.TEXT_NODE ) {
						if( !child.textContent ) return;
						const frag = document.createDocumentFragment();
						if( isEn ) { fillEn(frag, child.textContent); } else { fillJp(frag, child.textContent); }
						child.replaceWith(frag);
					} else if( child.nodeType === Node.ELEMENT_NODE && child.tagName !== 'BR' ) {
						walk(child);
					}
				});
			};
			walk(root);
		});

		// 英字の黄色い下線は、その見出しの最後の文字が入り終わってから引く
		const en = group.querySelector('.top_ttlEn');
		if( en ) this.setMarkerIndex(en);
		// 伏せCSSを有効化（JSが割ったときだけ。no-JS/reduced-motion では素の表示）
		group.classList.add('is-split');
	}
}

/**
 * FV
 *  - 読み込み後に is-loaded を付けて登場アニメーションを開始する
 *  - メインビジュアルの写真を切り替える（設計「画像切り替わりフェード」）
 */
class Fv {
	constructor(){
		this.fv = document.querySelector('.top_fv');
		if( !this.fv ) return;
		this.render();
	}

	render(){
		this.start();
		new FvSlider(this.fv);
		new FvLabels(this.fv);
	}

	// html.is-opening が付いていれば幕（Opening）から。無ければ従来どおり即表示。
	// どちらも最後に reveal() で登場アニメを起動する。
	start(){
		if( app.html.classList.contains('is-opening') ) {
			new Opening(this.fv, ()=> this.reveal());
		} else {
			this.showWhenReady();
		}
	}

	// FVの登場（テキスト・ラベル・実績帯が立ち上がる）を起動する
	reveal(){
		this.fv.classList.add('is-visible');
		app.html.classList.add('is-loaded');
	}

	// 幕なしのとき：1枚目の画像が来てから登場させる（アークだけ先に出るのを防ぐ）
	showWhenReady(){
		const first = this.fv.querySelector('.top_fv_visualPhoto img');
		if( !first || first.complete ) {
			this.reveal();
		} else {
			first.addEventListener('load', ()=> this.reveal(), { once: true });
			first.addEventListener('error', ()=> this.reveal(), { once: true });
		}
	}
}

/**
 * オープニング（トップを開くたびに毎回・PC/SP共通）
 *  白ベタの幕の中央にプロシーズのロゴ。
 *  ロゴは c_pagettl_en（.c_splitChar）と同じ動きで登場し（is-play）、
 *  少し見せてから幕ごとフェードアウトして（is-out）、裏のFVへ引き渡す。
 *
 *  出す/出さないの判定は front-page.php の head インラインスクリプトが済ませ、
 *  ここは html.is-opening が付いているときだけ動く。動きはCSS、間だけJSが持つ。
 */
const OPENING_HOLD = 900;  // ロゴを見せている時間（登場0.5s + この間）
const OPENING_FADE = 600;  // 幕のフェードアウト（_top.scss の transition とそろえる）

class Opening {
	constructor(fv, onReveal){
		this.fv = fv;
		this.onReveal = onReveal;
		this.root = document.querySelector('.top_opening');
		this.logo = this.root && this.root.querySelector('.top_opening_logo');

		// 幕が無い/壊れている環境では素直に表示して印を消す
		if( !this.root || !this.logo ) {
			this.fallback();
			return;
		}
		this.startWhenReady();
	}

	fallback(){
		app.html.classList.remove('is-opening');
		if( this.onReveal ) this.onReveal();
	}

	// ロゴSVG（各スライスの背景画像）を読み込んでから動かす。
	// 空のスライスが滑り込むのを防ぐ。来なくても 1.2s で強制的に始める
	startWhenReady(){
		const go = ()=> this.play();
		const pre = new Image();
		pre.onload = go;
		pre.onerror = go;
		pre.src = '/assets/imgs/common/logo.svg';
		if( pre.complete ) go(); // キャッシュ済みなら即時
		setTimeout(()=>{ if( !this.started ) go(); }, 1200);
	}

	play(){
		if( this.started ) return;
		this.started = true;

		// ロゴを滑り込ませる（.c_splitChar と同じ動き）
		// 次のフレームで付けて、伏せ状態(opacity:0)からのトランジションを確実に走らせる
		requestAnimationFrame(()=> this.root.classList.add('is-play'));

		// 見せたあと、幕ごとフェードアウト
		setTimeout(()=>{
			if( this.onReveal ) this.onReveal(); // 裏のFVへ引き渡し
			this.root.classList.add('is-out');
			setTimeout(()=> this.done(), OPENING_FADE);
		}, OPENING_HOLD);
	}

	done(){
		if( this.finished ) return;
		this.finished = true;
		app.html.classList.remove('is-opening');
		if( this.root && this.root.parentNode ) this.root.parentNode.removeChild(this.root);
		// 幕ぶんの座標ずれを、スクロールモーション側に測り直させる
		ScrollTrigger.refresh();
	}
}

/**
 * AIラベルの文言送り
 *  「考えている → 答えが出る」を繰り返す。
 *  考えている間は文字の上を光がゆっくり流れ続ける（CSSの .is-thinking）。
 *  ふわふわ浮く動きとアイコンの回転も CSS 側（_top.scss）が担当し、
 *  ここでは文言と状態の切り替えタイミングだけを持つ。
 */
const LABEL_THINK = 4600;  // 考えている時間
const LABEL_ANSWER = 7400; // 答えを見せている時間

class FvLabels {
	constructor(fv){
		this.items = Array.from(fv.querySelectorAll('.top_fv_label'));
		if( !this.items.length ) return;
		if( window.matchMedia('(prefers-reduced-motion: reduce)').matches ) return;

		// [考え中, 答え] の組。順に送る
		this.scripts = {
			__progress: [
				['学習進捗を集計中…', '学習進捗 87%'],
				['受講ペースを確認中…', '今週は4講座 完了'],
				['得意分野を判定中…', '得意は「実務ケース」'],
				['修了時期を予測中…', '修了まで あと2週間'],
				['受講状況を照合中…', '継続率 94%']
			],
			__understand: [
				['理解度を分析中…', '理解度 92%'],
				['つまずきを抽出中…', '弱点は「用語の定義」'],
				['復習範囲を選定中…', '復習は3問だけ'],
				['定着度を測定中…', '1週間後の定着 78%'],
				['回答傾向を照らし中…', 'ケアレスミスが2割']
			],
			__comment: [
				['AIコメントを自動生成中…', '今週の伸び +12%'],
				['次の一手を考え中…', '明日の課題を用意しました'],
				['学習ログを要約中…', '要点を3行にまとめました'],
				['原因を推定中…', '前提知識の復習を提案'],
				['受講者へ言葉を選び中…', '「この調子で進めましょう」']
			]
		};

		this.render();
	}

	render(){
		this.items.forEach((el, i)=>{
			const key = Object.keys(this.scripts).find((k)=> el.classList.contains(k));
			const pairs = this.scripts[key];
			const txt = el.querySelector('.top_fv_labelTxt');
			if( !pairs || !txt ) return;

			// 3つが同時に動くと忙しないので、開始をずらす
			setTimeout(()=> this.loop(txt, pairs, 0), 2400 + i * 3600);
		});
	}

	// 考え中 → 答え → 次の組、を繰り返す
	loop(txt, pairs, index){
		const pair = pairs[index % pairs.length];

		// 答えを見せる
		setTimeout(()=>{
			this.swap(txt, pair[1], false);

			// 次の組の「考え中」へ
			setTimeout(()=>{
				const next = (index + 1) % pairs.length;
				this.swap(txt, pairs[next][0], true);
				this.loop(txt, pairs, next);
			}, LABEL_ANSWER);
		}, LABEL_THINK);
	}

	// 文言の差し替え。上へ抜けてから、下からふわっと戻す。
	// 併せてラベルの横幅も、今の幅から新しい幅へ滑らかに伸縮させる
	swap(txt, text, thinking){
		const label = txt.closest('.top_fv_label');
		// 変形の影響を受けない layout 幅で測る
		const from = label ? label.offsetWidth : 0;

		txt.classList.add('is-out');
		setTimeout(()=>{
			txt.textContent = text;
			txt.classList.toggle('is-thinking', thinking);
			// アイコンの回転を制御するため、親のラベルにも状態を持たせる
			if( label ) label.classList.toggle('is-thinking', thinking);
			txt.classList.remove('is-out');
			txt.classList.add('is-prep'); // 下にずらして透明（transitionなし）

			if( label ) {
				// 新しい文言での自然な幅を測ってから、今の幅を起点に伸縮させる
				label.style.transition = 'none';
				label.style.width = 'auto';
				const to = label.offsetWidth;
				label.style.width = from + 'px';
				void label.offsetWidth; // ここまでを即時反映させる
				label.style.transition = '';
				label.style.width = to + 'px';
				// 伸縮後は自動幅に戻す（フォント読み込みや画面幅の変化に追従させるため）
				setTimeout(()=>{ label.style.width = ''; }, 460);
			}

			// 次の描画で外すと、そこから定位置へ戻るアニメーションが走る
			setTimeout(()=> txt.classList.remove('is-prep'), 20);
		}, 240);
	}
}

/**
 * メインビジュアルのスライダー（既存の Swiper 4系を使用）
 *  - 新しい写真が古い写真の上に重なってフェードイン（背景は透けない）
 *  - 重ね順と不透明度はCSS側（.swiper-slide-active / -prev）で制御する
 *  - ページャーは設計 nav(3019:1509) の★4つ
 */
class FvSlider {
	constructor(fv){
		this.el = fv.querySelector('.top_fv_slider');
		this.pager = document.querySelector('.top_fv_pager');
		if( !this.el || typeof Swiper === 'undefined' ) return;
		if( this.el.querySelectorAll('.swiper-slide').length < 2 ) return;
		this.render();
	}

	render(){
		const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		// 以前 loop:true で初期化されたJSがキャッシュされていると複製スライドが
		// 残り、切り替えが重複したりページャーの数が合わなくなるため掃除しておく
		this.el.querySelectorAll('.swiper-slide-duplicate').forEach((el)=> el.remove());

		this.swiper = new Swiper(this.el, {
			// スライドを重ねて配置するために fade を使うが、見た目の不透明度は
			// CSS側で持つ（Swiperの制御だと切り替え中に背景が透けるため）
			effect: 'fade',
			fadeEffect: { crossFade: false },
			// loop は複製スライドを作り、ページャーで飛んだときに
			// 表示が重複するので使わない（末尾からは先頭へ自動で戻る）
			loop: false,
			speed: 1200,
			allowTouchMove: false,
			autoplay: reduced ? false : {
				delay: 5000,
				disableOnInteraction: false
			},
			pagination: this.pager ? {
				el: this.pager,
				clickable: true,
				bulletClass: 'top_fv_pagerItem',
				bulletActiveClass: 'is-current'
			} : undefined
		});

		// 「直前に表示していたスライド」を自前で持つ。
		// Swiperの .swiper-slide-prev はDOM上の隣であって表示履歴ではないため、
		// ページャーで2つ以上飛ぶと関係ない写真が下に出てしまう
		const markPrev = ()=>{
			const slides = this.el.querySelectorAll('.swiper-slide');
			slides.forEach((el)=> el.classList.remove('is-prev'));
			const prev = slides[this.swiper.previousIndex];
			if( prev && this.swiper.previousIndex !== this.swiper.activeIndex ) {
				prev.classList.add('is-prev');
			}
		};
		this.swiper.on('slideChangeTransitionStart', markPrev);

		// 初期表示が終わってからフェードを有効にする
		setTimeout(()=> this.el.classList.add('is-ready'), 50);

		// スクロールモーションで写真の枠が縮むため、サイズ変化に追従させる
		app.Util.resize(()=>{
			if( this.swiper ) this.swiper.update();
		});
	}
}

/**
 * FV → What is e-Learning? のスクロールモーション（設計「スクロールモーション」1〜8）
 *
 *  FVのブランドマークと循環図の中心のマークは「同じもの」。
 *  スクロールに連動して、FVの位置・サイズから循環図の中の定位置へ
 *  回転させずに縮小しながら収める。FVとABOUTで一続きの絵になる。
 *
 *  実装:
 *    移動中は position:fixed の1要素として描画し、行き先（.top_about_cycle_mark）の
 *    座標は毎フレーム実測する。ABOUT自体もスクロールで動くため、
 *    行き先を固定値で持つと連動しない。
 *    到達したら本物のマークに引き渡す（同じ絵なので繋ぎ目は出ない）。
 */
// 写真とAIラベルが「縮みきる・透過しきる」進行度。
// マークは1.0で循環図に着地するので、その半分の時点で先に消え終わる
const EARLY = 0.5;

class FvScroll {
	constructor(cycle){
		this.cycle = cycle; // 着地したら循環図を出す
		this.fv = document.querySelector('.top_fv');
		this.about = document.querySelector('.top_about');
		if( !this.fv || !this.about ) return;

		this.graphic = this.fv.querySelector('.top_fv_visualGraphic');
		this.photo = this.fv.querySelector('.top_fv_visualPhoto');
		this.labels = this.fv.querySelector('.top_fv_labels');
		this.pager = this.fv.querySelector('.top_fv_pager');
		this.mark = this.about.querySelector('.top_about_cycle_mark');
		if( !this.graphic || !this.mark ) return;

		this.render();
	}

	// FV内でのマークの自然な位置・サイズ（fixed化する前の値）を控える
	measure(){
		const props = 'position,left,top,width,height,zIndex,margin';
		gsap.set(this.graphic, { clearProps: props });
		if( this.photo ) gsap.set(this.photo, { clearProps: props });
		if( this.labels ) gsap.set(this.labels, { clearProps: props + ',transform,transformOrigin' });
		if( this.pager ) gsap.set(this.pager, { clearProps: props + ',right,transform,transformOrigin' });

		const fv = this.fv.getBoundingClientRect();
		const g = this.graphic.getBoundingClientRect();
		this.nat = { x: g.left - fv.left, y: g.top - fv.top, w: g.width, h: g.height };

		// 写真はマークの枠内での相対位置で持つ。こうしておけば
		// マークを縮めるだけで写真も同じ比率で縮む
		if( this.photo ) {
			const ph = this.photo.getBoundingClientRect();
			this.natPhoto = {
				rx: (ph.left - g.left) / g.width,
				ry: (ph.top - g.top) / g.height,
				rw: ph.width / g.width,
				rh: ph.height / g.height
			};
		}

		// ページャーも同じくマーク枠内の相対位置で持つ。
		// 中身（★の間隔）が潰れないよう、大きさは幅指定ではなく scale で変える
		if( this.pager ) {
			const pg = this.pager.getBoundingClientRect();
			this.natPager = {
				rx: (pg.left - g.left) / g.width,
				ry: (pg.top - g.top) / g.height,
				w: pg.width,
				h: pg.height
			};
		}
	}

	// 進行度に応じて、FV位置 → 循環図の中の定位置 へ補間する
	tick(p){
		if( !this.nat ) return;
		const fv = this.fv.getBoundingClientRect();
		const to = this.mark.getBoundingClientRect(); // 行き先は毎回実測（ABOUTも動くため）
		const fromX = fv.left + this.nat.x;
		const fromY = fv.top + this.nat.y;
		const w = this.nat.w + (to.width - this.nat.w) * p;

		const x = fromX + (to.left - fromX) * p;
		const y = fromY + (to.top - fromY) * p;
		const h = w * (this.nat.h / this.nat.w);

		gsap.set(this.graphic, {
			position: 'fixed',
			margin: 0,
			zIndex: 20, // ABOUTの背景より前に出す
			left: x, top: y, width: w, height: h
		});

		// 写真とAIラベルは「移動はマークと同じ」。縮小だけマークより先行させるため、
		// マークの現在の枠を基準に、追加の縮小率 s を中心基準で掛ける
		const pf = Math.min(1, p / EARLY);
		const wf = this.nat.w + (to.width - this.nat.w) * pf;
		const s = w > 0 ? wf / w : 1;

		// 写真: マーク枠内の相対位置に置き、そこから中心へ向かって余分に縮める
		if( this.photo && this.natPhoto ) {
			const pw = this.natPhoto.rw * w;
			const ph2 = this.natPhoto.rh * h;
			gsap.set(this.photo, {
				position: 'fixed',
				margin: 0,
				zIndex: 21, // マークより前（FVでの重なり順を保つ）
				left: x + this.natPhoto.rx * w + pw * (1 - s) / 2,
				top: y + this.natPhoto.ry * h + ph2 * (1 - s) / 2,
				width: pw * s,
				height: ph2 * s
			});
		}

		// AIラベル: 枠ごと拡大縮小する（文字サイズも一緒に縮む）。
		// 位置はマークの枠に合わせ、余分な縮小ぶんは中心に寄せて相殺する
		if( this.labels ) {
			gsap.set(this.labels, {
				position: 'fixed',
				margin: 0,
				zIndex: 22,
				left: x + w * (1 - s) / 2,
				top: y + h * (1 - s) / 2,
				width: this.nat.w,
				height: this.nat.h,
				transformOrigin: '0 0',
				scale: (w / this.nat.w) * s
			});
		}

		// ページャーも写真と同じ扱い。マークと一緒に動きながら、同じ率で縮む
		if( this.pager && this.natPager ) {
			const boxScale = w / this.nat.w;      // マーク枠の倍率
			const scale = boxScale * s;           // 写真と同じ先行縮小を掛けた倍率
			const dx = this.natPager.w * boxScale * (1 - s) / 2;
			const dy = this.natPager.h * boxScale * (1 - s) / 2;
			gsap.set(this.pager, {
				position: 'fixed',
				margin: 0,
				right: 'auto', // CSSの right を無効化（left と両立すると幅が伸びる）
				zIndex: 22,
				left: x + this.natPager.rx * w + dx,
				top: y + this.natPager.ry * h + dy,
				transformOrigin: '0 0',
				scale: scale
			});
		}

		this.swap(p >= 1);
	}

	// 移動中の要素と、循環図の本物のマークを引き渡す
	swap(landed){
		gsap.set(this.mark, { opacity: landed ? 1 : 0 });
		gsap.set(this.graphic, { opacity: landed ? 0 : 1 });

		// 着地したあとも、これらは position:fixed のまま画面に残る（次に戻ってきた
		// ときのために座標を持たせ続けている）。opacity:0 でも当たり判定は生きて
		// いるので、そのままだと画面中央右あたりの 259x333 が透明な蓋になり、
		// 下のセクション（SERVICE/SOLUTION/CUSTOMER 等）のリンクが押せなくなる。
		// 着地中だけ画面から外す。戻ってきたら swap(false) で元に戻る。
		// pointer-events だけでは不足。Swiper(fade)が active スライドに
		// pointer-events:auto を当て直すので、visibility で確実に落とす
		[this.graphic, this.photo, this.pager].forEach((el)=>{
			if( el ) gsap.set(el, {
				visibility: landed ? 'hidden' : 'visible',
				pointerEvents: landed ? 'none' : 'auto'
			});
		});

		// マークが着地したら、そこで初めて循環図のパーツを出す
		if( landed && this.cycle ) this.cycle.play();
	}

	render(){
		const mm = gsap.matchMedia();

		// SPは配置が別なのでPCのみ
		mm.add('(min-width: 1024px)', ()=>{
			this.measure();
			gsap.set(this.mark, { opacity: 0 });

			// FVが画面から抜けるまでの間で移動しきる。
			// ピン留めしないので、ABOUTは通常どおりせり上がってくる（＝連動して見える）
			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: this.fv,
					start: 'top top',
					end: 'bottom top',
					scrub: true,
					invalidateOnRefresh: true,
					onRefresh: ()=>{ this.measure(); },
					onUpdate: (self)=> this.tick(self.progress),
					onLeave: ()=> this.tick(1),
					onEnterBack: ()=> this.swap(false)
				},
				defaults: { ease: 'none' }
			});

			// テキスト・ラベル・実績バー・写真は移動の序盤で退場。
			// 写真とラベルはマーク（最後まで残る）より早く透過しきる
			tl.to('.top_fv_body', { y: -60, opacity: 0, duration: 0.35 }, 0)
				.to('.top_fv_labels', { opacity: 0, duration: EARLY }, 0)
					.to('.top_fv_pager', { opacity: 0, duration: EARLY }, 0)
				.to('.top_fv_logoloop', { opacity: 0, duration: 0.25 }, 0)
				.to('.top_fv_visualPhoto', { opacity: 0, duration: EARLY }, 0)
				// タイムラインの総尺を1に固定する。これが無いと最長トゥイーンの長さで
				// 正規化され、duration がスクロール量の割合と一致しなくなる
				.to({}, { duration: 1 }, 0);

			this.tick(0);
		});
	}
}

/**
 * 循環図の登場
 *  マークが着地するまでパーツは伏せておき、着地したら
 *  ノード 01→06 →点線リング→方向の三角 の順に出す。
 *  SPはFVのモーションが無いので、循環図が見えたところで出す。
 */
class CycleReveal {
	constructor(){
		this.el = document.querySelector('.top_about_cycle');
		if( !this.el ) return;

		this.items = Array.from(this.el.querySelectorAll('.top_about_cycleItem'));
		this.ring = this.el.querySelector('.top_about_cycle_ring');
		this.arws = Array.from(this.el.querySelectorAll('.top_about_cycle_arw'));
		this.ripple = this.el.querySelector('.top_about_cycle_ripple');
		this.played = false;

		// 着地前は伏せておく（JSで隠すので、JSが動かない場合は出たまま）
		gsap.set(this.items, { opacity: 0, scale: 0.8 });
		gsap.set([this.ring, this.ripple].concat(this.arws), { opacity: 0 });

		// SPはマークの着地が無いため、見えた時点で出す
		if( !window.matchMedia('(min-width: 1024px)').matches ) {
			ScrollTrigger.create({
				trigger: this.el,
				start: 'top 80%',
				onEnter: ()=> this.play()
			});
		}
	}

	play(){
		if( this.played || !this.el ) return;
		this.played = true;

		gsap.timeline({ defaults: { ease: 'power2.out' } })
			// 01 から順に
			.to(this.items, { opacity: 1, scale: 1, duration: 0.5, stagger: 0.12 })
			// 続いて点線リング
			.to([this.ring, this.ripple], { opacity: 1, duration: 0.6 }, '-=0.15')
			// 最後に方向の三角
			.to(this.arws, { opacity: 1, duration: 0.5, stagger: 0.12 }, '-=0.2');
	}
}

/**
 * ABOUT の各要素の登場
 */
class AboutScroll {
	constructor(){
		this.about = document.querySelector('.top_about');
		if( !this.about ) return;
		this.render();
	}

	render(){
		const reveal = (target, vars, start)=>{
			const els = this.about.querySelectorAll(target);
			if( !els.length ) return;
			gsap.from(els, Object.assign({
				ease: 'power2.out',
				duration: 0.8,
				scrollTrigger: { trigger: this.about, start: start || 'top 70%' }
			}, vars));
		};

		reveal('.top_ttl, .top_about_lead, .top_about_txt', { y: 40, opacity: 0, stagger: 0.12 });
		// 循環図のパーツは CycleReveal（マークの着地に合わせる）が担当する
		reveal('.top_about_card', { y: 60, opacity: 0, stagger: 0.15 }, 'top 40%');
	}
}

export default Top;
